import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { convertDisplayToOptions } from "components/DesignSystem/Combobox/utils/convertDisplayToOptions";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { THREAD_FEEDBACK_RATING_DISPLAY } from "constants/threadChat";
import { FormikValues } from "formik";
import { useToast } from "hooks/useToast";
import { useAiThreadFeedbackMutation } from "store/apis/chat";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import FilingInfoIcon from "static/images/FilingInfo.svg";
import { AIAgentValidation } from "types/Models/reviewDocumentAndBalancingPayment";

export const ThreadManualAnalysis = ({
  isOpen,
  close,
  channelId,
  messageId,
  aiAgentValidation,
}: ModalProps & {
  channelId: string;
  messageId: string;
  aiAgentValidation?: AIAgentValidation;
}) => {
  const [aiThreadResponse, { isLoading }] = useAiThreadFeedbackMutation();
  const { alertToast, successToast } = useToast();
  const hasAdminFeedback = Boolean(aiAgentValidation?.thread_feedback_rating);

  const handleAIResponse = async (values: FormikValues) => {
    try {
      await aiThreadResponse({
        channelId,
        messageId,
        threadFeedbackRating: values.threadFeedbackRating,
        threadFeedbackText: values.threadFeedbackText,
      }).unwrap();
      successToast({
        title: "Submitted successfully",
        message: "Your feedback has been recorded",
      });
      close();
    } catch (error: any) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const ratingOptions = convertDisplayToOptions(THREAD_FEEDBACK_RATING_DISPLAY);

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      {isOpen && (
        <FormikForm
          onSubmit={handleAIResponse}
          enableReinitialize
          initialValues={{
            threadFeedbackRating:
              aiAgentValidation?.thread_feedback_rating || "",
            threadFeedbackText: aiAgentValidation?.thread_feedback_text || "",
          }}
        >
          {({ submitForm, values, setFieldValue }) => {
            return (
              <>
                <Modal.Content useCustomOverlay>
                  <Modal.Header>
                    <Modal.Title>Feedback for AI Thread</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-space-y-6">
                    <div>
                      <Label required>
                        Was the AI response satisfactory and helpful?
                      </Label>
                      <RadioGroup.Root
                        value={values.threadFeedbackRating}
                        onValueChange={(value) =>
                          setFieldValue("threadFeedbackRating", value)
                        }
                        className="t-flex t-flex-col t-gap-1.5"
                        name="threadFeedbackRating"
                        disabled={hasAdminFeedback}
                      >
                        {ratingOptions.map((option) => (
                          <RadioGroup.Item
                            key={option.value}
                            value={option.value}
                            disabled={hasAdminFeedback}
                          >
                            <span className="t-text-body-sm">
                              {option.label}
                            </span>
                          </RadioGroup.Item>
                        ))}
                      </RadioGroup.Root>
                    </div>
                    <TextArea
                      disabled={hasAdminFeedback}
                      label={
                        <div className="t-flex t-gap-1">
                          How were the responses from the AI?
                          <ToolTip
                            text={
                              <div className="t-text-body-sm">
                                Share feedback covering key aspects like{" "}
                                <b>coverage, correctness, tone</b>, and any{" "}
                                <b>irrelevant or extra details</b> in the
                                response to help us improve the AI.
                              </div>
                            }
                            propClass="t-w-64"
                          >
                            <img src={FilingInfoIcon} alt="FilingInfoIcon" />
                          </ToolTip>
                        </div>
                      }
                      name="threadFeedbackText"
                      placeholder="Eg. It answered the users queries and the tone is similar to that of the support team."
                      rows={6}
                    />
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button>Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isLoading}
                      disabled={
                        isLoading ||
                        !values.threadFeedbackRating ||
                        hasAdminFeedback
                      }
                    >
                      Submit
                    </Button>
                  </Modal.FooterButtonGroup>
                </Modal.Content>
              </>
            );
          }}
        </FormikForm>
      )}
    </Modal.Root>
  );
};
